import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api';
import { formatRoleLabel } from '../utils/roleManagement';
import SpadesSettingsFields from './SpadesSettingsFields';
import HeartsSettingsFields from './HeartsSettingsFields';
import GinSettingsFields from './GinSettingsFields';
import './LobbyEditor.css';

const DEFAULT_SPADES_SETTINGS = {
  autoLossAt: 0,
  winAt: 500,
  scoringMode: 'teams',
  bagLimit: 10,
  bagPenalty: 100,
  doubleNilEnabled: true,
};

const DEFAULT_HEARTS_SETTINGS = {
  winAt: 100,
  moonMode: 'shoot_moon',
  jackOfDiamondsBonus: false,
};

const DEFAULT_GIN_SETTINGS = {
  winAt: 100,
  knockMax: 10,
  ginBonus: 25,
  undercutBonus: 25,
};

const TABLE_CAP_NON_OWNER = 25;
const TABLE_CAP_OWNER = 9999;

const GAME_OPTIONS = [
  { id: 'spades', label: 'Spades', playable: true },
  { id: 'hearts', label: 'Hearts', playable: true },
  { id: 'gin', label: 'Gin', playable: true },
  { id: 'pinochle', label: 'Pinochle', playable: false },
];

const EMPTY_FORM = {
  slug: '',
  title: '',
  description: '',
  min_view_role: 'normal',
  min_site_role: 'normal',
  anon_can_view: false,
  is_public: true,
  required_club_id: '',
  hide_watcher_hands: false,
  lock_game_settings: false,
  default_spades_settings: { ...DEFAULT_SPADES_SETTINGS },
  default_hearts_settings: { ...DEFAULT_HEARTS_SETTINGS },
  default_gin_settings: { ...DEFAULT_GIN_SETTINGS },
  max_tables: 50,
  min_tables: 4,
  auto_spawn_tables: false,
  auto_spawn_max: 25,
  allowed_games: ['spades'],
  default_game: 'spades',
};

function LobbyEditor() {
  const [lobbies, setLobbies] = useState([]);
  const [siteRoles, setSiteRoles] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [isSiteOwner, setIsSiteOwner] = useState(false);
  const [tableCapNonOwner, setTableCapNonOwner] = useState(TABLE_CAP_NON_OWNER);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3500);
  };

  const loadLobbies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/admin/lobbies');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load lobbies');
      setLobbies(data.lobbies || []);
      setIsSiteOwner(!!data.meta?.isSiteOwner);
      if (Number.isFinite(data.meta?.tableCapNonOwner)) {
        setTableCapNonOwner(data.meta.tableCapNonOwner);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSiteRoles = useCallback(async () => {
    try {
      const res = await apiFetch('/admin/lobby-roles');
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const roles = (data.roles || []).map((r) => (
          typeof r === 'string' ? r : r.role_name
        )).filter(Boolean);
        setSiteRoles(roles);
      }
    } catch {
      setSiteRoles(['normal']);
    }
  }, []);

  const loadClubs = useCallback(async () => {
    try {
      const res = await apiFetch('/clubs');
      const data = await res.json().catch(() => ({}));
      if (res.ok) setClubs(data.clubs || []);
    } catch {
      setClubs([]);
    }
  }, []);

  useEffect(() => {
    loadLobbies();
    loadClubs();
    loadSiteRoles();
  }, [loadLobbies, loadClubs, loadSiteRoles]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const startEdit = (lobby) => {
    setEditingId(lobby.id);
    setForm({
      slug: lobby.slug || '',
      title: lobby.title || '',
      description: lobby.description || '',
      min_view_role: lobby.min_view_role || 'normal',
      min_site_role: lobby.min_site_role || 'normal',
      anon_can_view: !!lobby.anon_can_view,
      is_public: lobby.is_public !== false,
      required_club_id: lobby.required_club_id ? String(lobby.required_club_id) : '',
      hide_watcher_hands: !!lobby.hide_watcher_hands,
      lock_game_settings: !!lobby.lock_game_settings,
      default_spades_settings: {
        ...DEFAULT_SPADES_SETTINGS,
        ...(lobby.default_spades_settings || {}),
      },
      default_hearts_settings: {
        ...DEFAULT_HEARTS_SETTINGS,
        ...(lobby.default_hearts_settings || {}),
      },
      default_gin_settings: {
        ...DEFAULT_GIN_SETTINGS,
        ...(lobby.default_gin_settings || {}),
      },
      max_tables: lobby.max_tables ?? 50,
      min_tables: lobby.min_tables ?? 4,
      auto_spawn_tables: !!lobby.auto_spawn_tables,
      auto_spawn_max: lobby.auto_spawn_max ?? 25,
      allowed_games: Array.isArray(lobby.allowed_games) && lobby.allowed_games.length
        ? lobby.allowed_games
        : ['spades'],
      default_game: lobby.default_game || 'spades',
    });
    setError(null);
    setSuccess(null);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const maxTablesCap = isSiteOwner ? TABLE_CAP_OWNER : tableCapNonOwner;

  const clampTableCount = (field, value, maxTables = form.max_tables) => {
    const ceiling = Math.max(0, Math.floor(Number(maxTables) || 0));
    let n = Math.max(0, Math.floor(Number(value) || 0));
    if (field === 'max_tables') {
      return Math.min(n, maxTablesCap);
    }
    if (field === 'auto_spawn_max') {
      return Math.min(n, maxTablesCap, ceiling);
    }
    if (field === 'min_tables') {
      return Math.min(n, ceiling);
    }
    return n;
  };

  const handleCountFieldChange = (field, raw) => {
    if (raw === '' || raw === '-') return;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return;
    if (field === 'max_tables') {
      const nextMax = clampTableCount('max_tables', n);
      setForm((prev) => ({
        ...prev,
        max_tables: nextMax,
        min_tables: clampTableCount('min_tables', prev.min_tables, nextMax),
        auto_spawn_max: clampTableCount('auto_spawn_max', prev.auto_spawn_max, nextMax),
      }));
      return;
    }
    handleFieldChange(field, clampTableCount(field, n));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.slug.trim() || !form.title.trim()) {
      setError('Slug and title are required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    const body = {
      slug: form.slug.trim(),
      title: form.title.trim(),
      description: form.description.trim(),
      min_view_role: form.min_view_role,
      min_site_role: form.min_site_role,
      anon_can_view: form.anon_can_view,
      is_public: form.is_public,
      required_club_id: form.required_club_id ? Number(form.required_club_id) : null,
      hide_watcher_hands: form.hide_watcher_hands,
      lock_game_settings: form.lock_game_settings,
      default_spades_settings: form.default_spades_settings,
      default_hearts_settings: form.default_hearts_settings,
      default_gin_settings: form.default_gin_settings,
      max_tables: clampTableCount('max_tables', form.max_tables),
      min_tables: form.auto_spawn_tables
        ? clampTableCount('min_tables', form.min_tables)
        : 0,
      auto_spawn_tables: form.auto_spawn_tables,
      auto_spawn_max: form.auto_spawn_tables
        ? clampTableCount('auto_spawn_max', form.auto_spawn_max)
        : 0,
      allowed_games: form.allowed_games?.length ? form.allowed_games : ['spades'],
      default_game: form.allowed_games?.includes(form.default_game)
        ? form.default_game
        : (form.allowed_games?.[0] || 'spades'),
    };

    try {
      const res = editingId
        ? await apiFetch(`/admin/lobbies/${editingId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        : await apiFetch('/admin/lobbies', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to save lobby');

      flash(editingId ? 'Lobby updated' : 'Lobby created');
      resetForm();
      await loadLobbies();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const roleOptions = [...new Set([
    ...siteRoles,
    form.min_site_role,
    form.min_view_role,
  ].filter(Boolean))];

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiFetch(`/admin/lobbies/${deleteTarget.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to delete lobby');
      flash(`Deleted ${deleteTarget.title}`);
      if (editingId === deleteTarget.id) resetForm();
      setDeleteTarget(null);
      await loadLobbies();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="lobby-editor-section">
      <h2>Lobby Editor</h2>
      <p className="lobby-editor-hint">
        Manage public lobbies, visibility rules, and join requirements.
      </p>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="lobby-editor-layout">
        <form className="lobby-editor-form" onSubmit={handleSubmit}>
          <h3>{editingId ? 'Edit lobby' : 'Add lobby'}</h3>

          <label className="lobby-field">
            <span>Slug</span>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => handleFieldChange('slug', e.target.value)}
              placeholder="main"
              required
            />
          </label>

          <label className="lobby-field">
            <span>Title</span>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="Main Lobby"
              required
            />
          </label>

          <label className="lobby-field">
            <span>Description</span>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Optional description"
            />
          </label>

          <label className="lobby-field">
            <span>Min view role</span>
            <select
              value={form.min_view_role}
              onChange={(e) => handleFieldChange('min_view_role', e.target.value)}
            >
              {roleOptions.map((role) => (
                <option key={role} value={role}>{formatRoleLabel(role)}</option>
              ))}
            </select>
          </label>

          <label className="lobby-field">
            <span>Min site role (join/access)</span>
            <select
              value={form.min_site_role}
              onChange={(e) => handleFieldChange('min_site_role', e.target.value)}
            >
              {roleOptions.map((role) => (
                <option key={role} value={role}>{formatRoleLabel(role)}</option>
              ))}
            </select>
          </label>

          <label className="lobby-checkbox">
            <input
              type="checkbox"
              checked={form.anon_can_view}
              onChange={(e) => handleFieldChange('anon_can_view', e.target.checked)}
            />
            <span>Anonymous users can view in list</span>
          </label>

          <label className="lobby-checkbox">
            <input
              type="checkbox"
              checked={form.is_public}
              onChange={(e) => handleFieldChange('is_public', e.target.checked)}
            />
            <span>Public lobby</span>
          </label>

          <label className="lobby-checkbox">
            <input
              type="checkbox"
              checked={form.hide_watcher_hands}
              onChange={(e) => handleFieldChange('hide_watcher_hands', e.target.checked)}
            />
            <span>Hide cards from watchers (tournament mode)</span>
          </label>

          <div className="lobby-editor-settings-block">
            <h4>Games</h4>
            <p className="lobby-editor-settings-hint">
              One game = locked to that type. Multiple games = host chooses when they sit.
              Spades, Hearts, and Gin are live. Pinochle is coming soon.
            </p>
            <div className="lobby-game-checks">
              {GAME_OPTIONS.map((g) => {
                const checked = form.allowed_games.includes(g.id);
                return (
                  <label key={g.id} className="lobby-checkbox">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setForm((prev) => {
                          let next = e.target.checked
                            ? [...prev.allowed_games, g.id]
                            : prev.allowed_games.filter((id) => id !== g.id);
                          if (!next.length) next = ['spades'];
                          const default_game = next.includes(prev.default_game)
                            ? prev.default_game
                            : next[0];
                          return { ...prev, allowed_games: next, default_game };
                        });
                      }}
                    />
                    <span>
                      {g.label}
                      {!g.playable ? ' (coming soon)' : ''}
                    </span>
                  </label>
                );
              })}
            </div>
            {form.allowed_games.length > 1 && (
              <label className="lobby-field">
                <span>Default game (pre-select for host)</span>
                <select
                  value={form.default_game}
                  onChange={(e) => handleFieldChange('default_game', e.target.value)}
                >
                  {form.allowed_games.map((id) => {
                    const g = GAME_OPTIONS.find((x) => x.id === id);
                    return (
                      <option key={id} value={id}>
                        {g?.label || id}
                      </option>
                    );
                  })}
                </select>
              </label>
            )}
          </div>

          <div className="lobby-editor-settings-block">
            <h4>Table layout</h4>
            <p className="lobby-editor-settings-hint">
              Tables are saved to the database and survive server restarts.
              {!isSiteOwner && ` Editors are limited to ${tableCapNonOwner} tables maximum.`}
            </p>
            <label className="lobby-field">
              <span>
                Max tables (hard ceiling)
                {isSiteOwner ? '' : ` — max ${tableCapNonOwner}`}
              </span>
              <input
                type="number"
                min={0}
                max={maxTablesCap}
                step={1}
                value={form.max_tables}
                onChange={(e) => handleCountFieldChange('max_tables', e.target.value)}
              />
            </label>
            <label className="lobby-checkbox">
              <input
                type="checkbox"
                checked={form.auto_spawn_tables}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setForm((prev) => ({
                    ...prev,
                    auto_spawn_tables: enabled,
                    min_tables: enabled && prev.min_tables === 0 ? 4 : prev.min_tables,
                    auto_spawn_max: enabled && prev.auto_spawn_max === 0 ? 25 : prev.auto_spawn_max,
                  }));
                }}
              />
              <span>Auto-spawn tables when existing tables fill up</span>
            </label>
            <label className={`lobby-field${form.auto_spawn_tables ? '' : ' lobby-field--disabled'}`}>
              <span>
                Minimum tables (always kept open)
                {form.auto_spawn_tables ? ' — set to 0 for chat-only' : ''}
              </span>
              <input
                type="number"
                min={0}
                max={form.max_tables}
                step={1}
                value={form.min_tables}
                onChange={(e) => handleCountFieldChange('min_tables', e.target.value)}
                disabled={!form.auto_spawn_tables}
              />
            </label>
            <label className={`lobby-field${form.auto_spawn_tables ? '' : ' lobby-field--disabled'}`}>
              <span>
                Auto-spawn up to
                {isSiteOwner
                  ? ' (cannot exceed max tables)'
                  : ` (max ${tableCapNonOwner})`}
              </span>
              <input
                type="number"
                min={0}
                max={Math.min(maxTablesCap, form.max_tables)}
                step={1}
                value={form.auto_spawn_max}
                onChange={(e) => handleCountFieldChange('auto_spawn_max', e.target.value)}
                disabled={!form.auto_spawn_tables}
              />
            </label>
            <p className="lobby-editor-settings-hint">
              {form.auto_spawn_tables
                ? 'Auto-spawn opens tables when existing ones fill. Empty tables are removed after 10 minutes, but never below the minimum.'
                : 'Manual table mode: auto-spawn, minimum tables, and cleanup are all off. Tables are only added or removed by hosts/admins.'}
            </p>
          </div>

          {form.allowed_games.includes('spades') && (
            <div className="lobby-editor-settings-block">
              <h4>Spades game settings</h4>
              <p className="lobby-editor-settings-hint">
                New tables inherit these Spades settings. Hosts can adjust them unless locked below.
                (Hidden when Spades is not enabled for this lobby.)
              </p>
              <SpadesSettingsFields
                settings={form.default_spades_settings}
                onChange={(next) => handleFieldChange('default_spades_settings', next)}
                className="lobby-editor-spades-settings"
                idPrefix="lobby-editor"
              />
            </div>
          )}

          {form.allowed_games.includes('hearts') && (
            <div className="lobby-editor-settings-block">
              <h4>Hearts game settings</h4>
              <p className="lobby-editor-settings-hint">
                New tables inherit these Hearts settings. Hosts can adjust them unless locked below.
              </p>
              <HeartsSettingsFields
                settings={form.default_hearts_settings}
                onChange={(next) => handleFieldChange('default_hearts_settings', next)}
                idPrefix="lobby-editor-hearts"
              />
            </div>
          )}

          {form.allowed_games.includes('gin') && (
            <div className="lobby-editor-settings-block">
              <h4>Gin settings</h4>
              <p className="lobby-editor-settings-hint">
                Two-player. New tables inherit these defaults.
              </p>
              <GinSettingsFields
                settings={form.default_gin_settings}
                onChange={(next) => handleFieldChange('default_gin_settings', next)}
                idPrefix="lobby-editor-gin"
              />
            </div>
          )}

          {(form.allowed_games.includes('spades')
            || form.allowed_games.includes('hearts')
            || form.allowed_games.includes('gin')) && (
            <div className="lobby-editor-settings-block">
              <label className="lobby-checkbox">
                <input
                  type="checkbox"
                  checked={form.lock_game_settings}
                  onChange={(e) => handleFieldChange('lock_game_settings', e.target.checked)}
                />
                <span>Lock settings at tables (tournament — host must confirm lock to start)</span>
              </label>
            </div>
          )}

          <label className="lobby-field">
            <span>Required club (private club lobby)</span>
            <select
              value={form.required_club_id}
              onChange={(e) => handleFieldChange('required_club_id', e.target.value)}
            >
              <option value="">None — open to all eligible users</option>
              {clubs.map((club) => (
                <option key={club.id} value={String(club.id)}>
                  {club.name}
                </option>
              ))}
            </select>
          </label>

          <div className="lobby-form-actions">
            <button type="submit" className="lobby-btn" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add lobby'}
            </button>
            {editingId && (
              <button type="button" className="lobby-btn lobby-btn--secondary" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>

        <div className="lobby-list-panel">
          <h3>Existing lobbies</h3>
          {loading ? (
            <p className="lobby-loading">Loading…</p>
          ) : lobbies.length === 0 ? (
            <p className="lobby-empty">No lobbies configured yet.</p>
          ) : (
            <ul className="lobby-admin-list">
              {lobbies.map((lobby) => (
                <li key={lobby.id} className="lobby-admin-item">
                  <div className="lobby-admin-item-head">
                    <strong>{lobby.title}</strong>
                    <span className="lobby-admin-slug">{lobby.slug}</span>
                  </div>
                  {lobby.description && (
                    <p className="lobby-admin-desc">{lobby.description}</p>
                  )}
                  <div className="lobby-admin-meta">
                    View: {formatRoleLabel(lobby.min_view_role)}
                    {' · '}
                    Join: {formatRoleLabel(lobby.min_site_role)}
                    {lobby.anon_can_view ? ' · anon OK' : ''}
                    {!lobby.is_public ? ' · private' : ''}
                    {lobby.required_club_id
                      ? ` · club #${lobby.required_club_id}`
                      : ''}
                    {lobby.lock_game_settings ? ' · tournament settings' : ''}
                    {` · max ${lobby.max_tables ?? 50} tables`}
                    {lobby.auto_spawn_tables
                      ? ` · min ${lobby.min_tables ?? 4} · auto-spawn ${lobby.auto_spawn_max ?? 25}`
                      : ' · manual tables'}
                  </div>
                  <div className="lobby-admin-actions">
                    <button type="button" className="lobby-btn" onClick={() => startEdit(lobby)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="lobby-btn lobby-btn--danger"
                      onClick={() => setDeleteTarget(lobby)}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {deleteTarget && (
        <div
          className="lobby-delete-overlay"
          onClick={() => !deleting && setDeleteTarget(null)}
          role="presentation"
        >
          <div
            className="lobby-delete-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3>Delete lobby?</h3>
            <p>
              Delete <strong>{deleteTarget.title}</strong> ({deleteTarget.slug})?
            </p>
            <div className="lobby-form-actions">
              <button
                type="button"
                className="lobby-btn lobby-btn--danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                className="lobby-btn lobby-btn--secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LobbyEditor;